import * as React from 'react'

// How wide a label actually is, so its ground can be sampled across the whole thing rather
// than down a column through the middle.
//
// Measuring text to POSITION a symbol is a mistake this repo has already made — mapbox balances
// its own line breaks and will not agree with a measurement taken out here. Sizing a SAMPLING
// WINDOW is a different question: disagreement moves which ground is averaged, not what is drawn.
//
// The cache must not outlive the font swap: measuring before Inter loads returns the fallback
// face's metrics, and a cached wrong width would describe every label for the session.

export interface ILabelTextBox {
  // Clamped to the wrap width, so it is the box mapbox will lay out rather than the run.
  width: number
  lines: number
}

const cache = new Map<string, ILabelTextBox>()
let context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null
let fontsReady = false

if (typeof document !== 'undefined' && document.fonts?.ready) {
  void document.fonts.ready.then(() => {
    fontsReady = true
    cache.clear()
  })
}

function measurer():
  | CanvasRenderingContext2D
  | OffscreenCanvasRenderingContext2D
  | null {
  if (context !== undefined) return context
  try {
    context =
      typeof OffscreenCanvas === 'function'
        ? new OffscreenCanvas(1, 1).getContext('2d')
        : document.createElement('canvas').getContext('2d')
  } catch {
    context = null
  }
  return context
}

// Rough enough to size a window with before the real face arrives, and never cached.
const FALLBACK_EM_PER_CHAR = 0.52

export function measureLabelTextBox(
  text: string,
  font: string,
  maxWidthPx: number,
  sizePx: number,
): ILabelTextBox {
  const key = `${font}|${maxWidthPx}|${text}`
  const hit = cache.get(key)
  if (hit) return hit

  const ctx = measurer()
  let run: number
  if (ctx) {
    ctx.font = font
    run = ctx.measureText(text).width
  } else {
    run = text.length * FALLBACK_EM_PER_CHAR * sizePx
  }

  const lines = Math.max(1, Math.ceil(run / Math.max(1, maxWidthPx)))
  // Mapbox balances wrapped lines to roughly equal width rather than filling each to the wrap
  // column, so a wrapped label is narrower than the column it wrapped at.
  const box: ILabelTextBox = {
    width: Math.max(1, lines > 1 ? run / lines : Math.min(run, maxWidthPx)),
    lines,
  }
  if (ctx && fontsReady) cache.set(key, box)
  return box
}

// True once fonts have loaded, so whatever was measured on the fallback face is measured again.
export function useLabelFontsReady(): boolean {
  const [ready, setReady] = React.useState(fontsReady)
  React.useEffect(() => {
    if (ready || typeof document === 'undefined' || !document.fonts?.ready) {
      return undefined
    }
    let isMounted = true
    void document.fonts.ready.then(() => {
      if (isMounted) setReady(true)
    })
    return () => {
      isMounted = false
    }
  }, [ready])
  return ready
}
